/**
 * MultiSelectDropdown - Multi-select component for inline table editing
 *
 * Uses the standard Autocomplete component with multiple selection for consistent styling.
 * Tracks selections by ID (matching option IDs) for reliable value/option matching.
 *
 * Saves on Enter key press or when clicking away from the cell.
 */

import { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react';
import { CaretDown } from '@phosphor-icons/react';
import { useEnterToClose } from '../hooks';
import { MultiselectEditorContainer } from '../styles';
import {
  MULTISELECT_INPUT_SX,
  MULTISELECT_PAPER_PROPS,
  MULTISELECT_POPPER_PROPS,
} from './multiselectInlineEditStyles';
import { renderMultiselectTags } from './multiselectInlineEditUtils';
import { TableAutocomplete } from './TableAutocomplete';

/**
 * Option type for the dropdown
 */
export type MultiSelectOption = {
  id: number | string;
  label: string;
  color?: string;
};

/**
 * Props for the MultiSelectDropdown component
 */
export type MultiSelectDropdownProps = {
  /** Available options to select from */
  options?: MultiSelectOption[];
  /** Currently selected values */
  value: MultiSelectOption[];
  /** Callback when selection changes - fires with full option objects */
  onChange: (newValue: MultiSelectOption[]) => void;
  /** Callback when dropdown closes */
  onClose?: () => void;
  /** Whether to auto-focus and open on mount */
  autoFocus?: boolean;
  /** Placeholder text when no selection */
  placeholder?: string;
  /** Minimum width of the dropdown */
  minWidth?: number;
};

/**
 * MultiSelectDropdown - A multi-select component that tracks selections by ID
 *
 * Key features:
 * - Uses standard Autocomplete for consistent styling
 * - Tracks selections by ID (stable, unique identifier)
 * - Returns full option objects with IDs for API calls
 * - Saves on Enter key press or click-away
 * - Shows selected values as deletable Chip tags
 * - Sorts options with selected first, then alphabetically
 */
export const MultiSelectDropdown = memo(function MultiSelectDropdown({
  options = [],
  value,
  onChange,
  onClose,
  autoFocus = false,
  placeholder = 'Select...',
  minWidth = 200,
}: MultiSelectDropdownProps) {
  // Ref to track if we're in an editing session - prevents useEffect from resetting state
  const isEditingRef = useRef(autoFocus);
  // Ref for the container element to detect clicks outside
  const containerRef = useRef<HTMLDivElement>(null);

  // Track selected IDs in internal state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => {
    return new Set(value.map(v => String(v.id)));
  });

  // Control the open state manually to prevent closing on blur
  const [open, setOpen] = useState(autoFocus);

  // Sync internal state when value prop changes
  // IMPORTANT: Only sync when NOT in an editing session to avoid overwriting user's selections
  useEffect(() => {
    if (!isEditingRef.current) {
      setSelectedIds(new Set(value.map(v => String(v.id))));
    }
  }, [value]);

  // Auto-set editing ref on mount if autoFocus is true
  useEffect(() => {
    if (autoFocus) {
      isEditingRef.current = true;
    }
  }, [autoFocus]);

  // Build final selection array with full option objects
  const buildFinalSelection = useCallback(() => {
    const finalSelection: MultiSelectOption[] = [];
    selectedIds.forEach(id => {
      const opt = options.find(o => String(o.id) === id);
      if (opt) {
        finalSelection.push(opt);
      } else {
        // Fallback: create option from id (for values not in current options)
        finalSelection.push({ id, label: id });
      }
    });
    return finalSelection;
  }, [selectedIds, options]);

  // Check if selection has changed from original value
  const hasSelectionChanged = useCallback(() => {
    const currentIds = new Set(value.map(v => String(v.id)));
    return selectedIds.size !== currentIds.size || [...selectedIds].some(id => !currentIds.has(id));
  }, [selectedIds, value]);

  // Handle save - called on Enter key or click-away
  const handleSave = useCallback(() => {
    if (hasSelectionChanged()) {
      onChange(buildFinalSelection());
    }
    isEditingRef.current = false;
    setOpen(false);
    onClose?.();
  }, [hasSelectionChanged, onChange, buildFinalSelection, onClose]);

  // Use capture-phase Enter key handler (runs before MUI's stopPropagation)
  useEnterToClose(open, handleSave);

  // Handle click outside to save and close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Check if click is inside the container (input area)
      if (containerRef.current?.contains(target)) {
        return;
      }

      // Check if click is inside the dropdown listbox (rendered in portal)
      if (target.closest('.MuiAutocomplete-listbox') || target.closest('.MuiAutocomplete-popper')) {
        return;
      }

      // Click is outside - save changes and close
      handleSave();
    };

    // Add listener on mount (capture phase for consistency with other event hooks)
    document.addEventListener('mousedown', handleClickOutside, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [handleSave]);

  // Compute selected options by matching IDs to options
  // This ensures we return options with correct data
  const selectedOptions = useMemo(() => {
    const result: MultiSelectOption[] = [];
    selectedIds.forEach(id => {
      // First try to find in options (to get full option data)
      const option = options.find(opt => String(opt.id) === id);
      if (option) {
        result.push(option);
      } else {
        // Fallback: create option from id (for values not in current options)
        result.push({ id, label: id });
      }
    });
    return result;
  }, [selectedIds, options]);

  // Handle selection change from TableAutocomplete
  const handleChange = useCallback((_: unknown, newValue: MultiSelectOption[] | null) => {
    isEditingRef.current = true;
    if (newValue) {
      setSelectedIds(new Set(newValue.map(v => String(v.id))));
    } else {
      setSelectedIds(new Set());
    }
  }, []);

  // Sync selection from value when opening so tags show correctly
  const handleOpen = useCallback(() => {
    setSelectedIds(new Set(value.map(v => String(v.id))));
    setOpen(true);
    isEditingRef.current = true;
  }, [value]);

  // Prevent default close behavior - we handle closing ourselves
  const handleAutocompleteClose = useCallback(
    (_event: React.SyntheticEvent, reason: string) => {
      // Only allow closing via escape key (handled by MUI)
      // Other close reasons (blur, selectOption with disableCloseOnSelect=false) are ignored
      if (reason === 'escape') {
        setSelectedIds(new Set(value.map(v => String(v.id))));
        isEditingRef.current = false;
        setOpen(false);
        onClose?.();
      }
      // For other reasons, keep the dropdown open
    },
    [value, onClose]
  );

  return (
    <MultiselectEditorContainer ref={containerRef}>
      <TableAutocomplete
        multiple
        open={open}
        onOpen={handleOpen}
        onClose={handleAutocompleteClose}
        options={options}
        value={selectedOptions}
        onChange={handleChange}
        sortSelectedFirst
        selectedValues={selectedOptions}
        isOptionEqualToValue={(option, val) => String(option.id) === String(val.id)}
        getOptionLabel={option => (typeof option === 'string' ? option : option?.label ?? '')}
        placeholder={placeholder}
        variant="standard"
        labelSeparate
        forcePopupIcon
        popupIcon={<CaretDown size={20} />}
        autoFocus={autoFocus}
        openOnFocus={autoFocus}
        disableCloseOnSelect
        renderTags={renderMultiselectTags}
        sx={{ ...MULTISELECT_INPUT_SX, minWidth }}
        componentsProps={{
          ...MULTISELECT_PAPER_PROPS,
          ...MULTISELECT_POPPER_PROPS,
        }}
      />
    </MultiselectEditorContainer>
  );
});
